import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { orderList } from "@/mocks/orderData";
import {
  getMainStatusSteps,
  statusStyle,
  type SubOrder,
  type CarInspectionPhoto,
} from "@/api/order";
import { ProgressBarByStep } from "@/components/ProgressBar";

// ── 顶部进度条 ──
function ProgressBar({ step, type }: { step: number; type?: string }) {
  const steps = getMainStatusSteps(type);
  return (
    <ProgressBarByStep
      step={step}
      titles={steps}
      theme="orange"
    />
  );
}

// ── 验车照片展示 ──
function InspectionPhotos({
  title,
  photos,
  color,
}: {
  title: string;
  photos: CarInspectionPhoto[];
  color: string;
}) {
  const [viewIdx, setViewIdx] = useState<number | null>(null);
  return (
    <div className="mt-2">
      <div className="flex items-center gap-1.5 mb-2">
        <i className="ri-camera-line" style={{ color, fontSize: 13 }} />
        <span className="text-xs font-semibold" style={{ color }}>
          {title}
        </span>
        <span className="text-xs text-gray-400">（司机已上传）</span>
      </div>
      <div className="flex gap-2">
        {photos.map((ph, i) => (
          <button
            key={i}
            onClick={() => setViewIdx(i)}
            className="relative rounded-xl overflow-hidden cursor-pointer flex-1"
            style={{ height: 72 }}
          >
            <img
              src={ph.url}
              alt={ph.label}
              className="w-full h-full object-cover"
            />
            <div
              className="absolute bottom-0 left-0 right-0 text-center py-0.5 text-white"
              style={{ background: "rgba(0,0,0,0.45)", fontSize: 10 }}
            >
              {ph.label}
            </div>
          </button>
        ))}
      </div>
      {viewIdx !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: "rgba(0,0,0,0.9)" }}
          onClick={() => setViewIdx(null)}
        >
          <div className="relative w-full max-w-sm px-4">
            <img
              src={photos[viewIdx].url}
              alt=""
              className="w-full rounded-2xl"
            />
            <div className="text-center text-white text-sm mt-3">
              {photos[viewIdx].label}
            </div>
            <button
              className="absolute top-2 right-6 w-8 h-8 flex items-center justify-center rounded-full cursor-pointer"
              style={{ background: "rgba(255,255,255,0.15)" }}
              onClick={() => setViewIdx(null)}
            >
              <i
                className="ri-close-line text-white"
                style={{ fontSize: 18 }}
              />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── 子订单类型配置 ──
const subTypeConfig: Record<
  string,
  {
    steps: string[];
    actionsByStatus: Record<string, string[]>;
    navPath: string;
    typeLabel: string;
    typeIcon: string;
  }
> = {
  hotel: {
    steps: ["待酒店确认", "已确认·待入住", "已入住", "已离店"],
    actionsByStatus: {
      待酒店确认: ["取消订单", "改期"],
      "已确认·待入住": ["取消订单", "改期"],
      已入住: ["延住", "联系酒店"],
      已离店: ["售后", "开票", "评价"],
      已完成: ["售后", "开票", "评价"],
      已取消: [],
      售后中: ["补充凭证"],
    },
    navPath: "/hotel-detail-product",
    typeLabel: "酒店",
    typeIcon: "ri-hotel-bed-line",
  },
  car: {
    steps: ["待派车", "待取车", "使用中", "已还车"],
    actionsByStatus: {
      待派车: ["取消订单", "改期", "换车型"],
      待取车: ["换车型", "联系司机"],
      使用中: ["延期", "联系司机"],
      已还车: ["售后", "开票", "评价"],
      已完成: ["售后", "开票", "评价"],
      已取消: [],
      售后中: ["补充凭证"],
    },
    navPath: "/car-detail-product",
    typeLabel: "租车",
    typeIcon: "ri-car-line",
  },
  tour: {
    steps: ["待成团", "已成团·待出发", "行程中", "已完成"],
    actionsByStatus: {
      待成团: ["取消订单", "改期"],
      "已成团·待出发": ["取消订单", "改期"],
      行程中: ["联系导游"],
      已完成: ["售后", "开票", "评价"],
      已完结: ["售后", "开票", "评价"],
      已取消: [],
      售后中: ["补充凭证"],
    },
    navPath: "/route-detail-product",
    typeLabel: "线路",
    typeIcon: "ri-route-line",
  },
  guide: {
    steps: ["待确认", "已确认·待服务", "服务中", "已完成"],
    actionsByStatus: {
      待确认: ["取消订单"],
      "已确认·待服务": ["取消订单", "改期"],
      服务中: ["联系导游"],
      已完成: ["售后", "开票", "评价"],
      已取消: [],
      售后中: ["补充凭证"],
    },
    navPath: "/guide-detail-product",
    typeLabel: "导游",
    typeIcon: "ri-user-star-line",
  },
};

// ── 规则标签 ──
const subRuleTags: Record<string, string> = {
  hotel: "提前3天免费取消",
  car: "提前24小时免费取消",
  route: "不成团自动取消",
  guide: "提前7天免费取消",
};

// ── 子订单开票弹窗 ──
function SubInvoiceModal({
  subName,
  subAmount,
  onClose,
  onSubmitSuccess,
}: {
  subName: string;
  subAmount: number;
  onClose: () => void;
  onSubmitSuccess: () => void;
}) {
  const [invoiceType, setInvoiceType] = useState<"personal" | "company">(
    "personal",
  );
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({ title: "", taxId: "", email: "" });
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }}
    >
      <div className="bg-white rounded-t-3xl w-full" style={{ maxWidth: 480 }}>
        <div className="w-10 h-1 rounded-full bg-gray-200 mx-auto mt-3" />
        <div
          className="flex items-center justify-between px-5 pt-4 pb-3"
          style={{ borderBottom: "1px solid #F5F5F5" }}
        >
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer"
            style={{ background: "#F4F5F9" }}
          >
            <i
              className="ri-close-line"
              style={{ color: "#374151", fontSize: 18 }}
            />
          </button>
          <span className="text-base font-bold text-gray-900">申请开票</span>
          <div className="w-8" />
        </div>
        <div className="px-5 py-5">
          {submitted ? (
            <div className="flex flex-col items-center py-6">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mb-3"
                style={{ background: "#FFF7ED" }}
              >
                <i
                  className="ri-bill-line"
                  style={{ color: "#D97706", fontSize: 32 }}
                />
              </div>
              <div className="text-base font-bold text-gray-900 mb-1">
                开票申请已提交
              </div>
              <div className="text-xs text-gray-500 text-center mb-1">
                产品：{subName}
              </div>
              <div className="text-xs text-gray-500 text-center">
                电子发票将在1-3个工作日内发送至您的邮箱：{formData.email}
              </div>
              <button
                onClick={onSubmitSuccess}
                className="mt-5 px-8 py-2.5 rounded-full text-sm font-semibold text-white cursor-pointer whitespace-nowrap"
                style={{ background: "#D97706" }}
              >
                知道了
              </button>
            </div>
          ) : (
            <>
              <div
                className="mb-3 p-3 rounded-xl text-xs text-gray-500"
                style={{ background: "#F8F8F8" }}
              >
                开票产品：
                <span className="font-medium text-gray-700">{subName}</span>
                <span className="ml-2 font-bold" style={{ color: "#EF4444" }}>
                  ¥{subAmount.toLocaleString()}
                </span>
              </div>
              <div className="flex gap-2 mb-4">
                {[
                  { val: "personal" as const, label: "个人发票" },
                  { val: "company" as const, label: "企业发票" },
                ].map(({ val, label }) => (
                  <button
                    key={val}
                    onClick={() => setInvoiceType(val)}
                    className="flex-1 py-2.5 rounded-xl text-sm cursor-pointer font-medium whitespace-nowrap"
                    style={{
                      background: invoiceType === val ? "#FFF7ED" : "#F4F5F9",
                      color: invoiceType === val ? "#D97706" : "#374151",
                      border:
                        invoiceType === val
                          ? "1.5px solid #FED7AA"
                          : "1.5px solid transparent",
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="space-y-3 mb-5">
                <div>
                  <div className="text-xs text-gray-500 mb-1.5">
                    {invoiceType === "company" ? "企业名称" : "发票抬头"}
                  </div>
                  <input
                    className="w-full rounded-xl px-3 py-2.5 text-sm outline-none"
                    style={{
                      background: "#F8F8F8",
                      border: "1.5px solid #F0F0F0",
                    }}
                    placeholder={
                      invoiceType === "company"
                        ? "请输入企业名称"
                        : "请输入您的姓名"
                    }
                    value={formData.title}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, title: e.target.value }))
                    }
                  />
                </div>
                {invoiceType === "company" && (
                  <div>
                    <div className="text-xs text-gray-500 mb-1.5">
                      税务识别号
                    </div>
                    <input
                      className="w-full rounded-xl px-3 py-2.5 text-sm outline-none"
                      style={{
                        background: "#F8F8F8",
                        border: "1.5px solid #F0F0F0",
                      }}
                      placeholder="请输入18位税务识别号"
                      value={formData.taxId}
                      onChange={(e) =>
                        setFormData((p) => ({ ...p, taxId: e.target.value }))
                      }
                    />
                  </div>
                )}
                <div>
                  <div className="text-xs text-gray-500 mb-1.5">接收邮箱</div>
                  <input
                    className="w-full rounded-xl px-3 py-2.5 text-sm outline-none"
                    style={{
                      background: "#F8F8F8",
                      border: "1.5px solid #F0F0F0",
                    }}
                    placeholder="请输入接收发票的邮箱"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, email: e.target.value }))
                    }
                  />
                </div>
              </div>
              <button
                onClick={() => {
                  if (formData.title && formData.email) setSubmitted(true);
                }}
                className="w-full py-3 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap"
                style={{
                  background:
                    formData.title && formData.email ? "#D97706" : "#F0F0F0",
                  color: formData.title && formData.email ? "#fff" : "#9CA3AF",
                }}
              >
                提交开票申请
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── 子订单卡片 ──
function SubOrderCard({
  sub,
  onAfterSale,
  isAfterSaleApplied,
  isInvoiceApplied,
  onInvoiceApplied,
}: {
  sub: SubOrder;
  onAfterSale: (subId: string) => void;
  isAfterSaleApplied?: boolean;
  isInvoiceApplied?: boolean;
  onInvoiceApplied?: (subId: string) => void;
}) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const cfg = subTypeConfig[sub.type] ?? subTypeConfig.tour;
  const steps = cfg.steps;
  const currentStepIdx = steps.findIndex(
    (s) => s === sub.subStatus || sub.subStatus.startsWith(s.split("·")[0]),
  );
  const rawActions = cfg.actionsByStatus[sub.subStatus] ?? [];
  const actions = rawActions
    .map((a) => {
      if (a === "售后" && isAfterSaleApplied) return "__after_sale_done__";
      if (a === "开票" && isInvoiceApplied) return "__invoice_done__";
      if (a === "售后" && isInvoiceApplied) return null;
      if (a === "开票" && isAfterSaleApplied) return null;
      return a;
    })
    .filter((a): a is string => a !== null);

  const renderDetail = () => {
    if (sub.type === "hotel") {
      return (
        <div className="mt-1 space-y-0.5">
          <div className="text-xs text-gray-500">{sub.spec}</div>
          <div className="text-xs" style={{ color: "#9CA3AF" }}>
            {sub.subStatusNote ?? sub.dateRange}
          </div>
        </div>
      );
    }
    if (sub.type === "car") {
      return (
        <div className="mt-1 space-y-0.5">
          <div className="text-xs text-gray-500">{sub.dateRange}</div>
          <div className="text-xs" style={{ color: "#9CA3AF" }}>
            {sub.subStatusNote}
          </div>
        </div>
      );
    }
    if (sub.type === "tour") {
      return (
        <div className="mt-1 space-y-0.5">
          <div className="text-xs text-gray-500">{sub.spec}</div>
          <div className="text-xs" style={{ color: "#9CA3AF" }}>
            {sub.dateRange}
          </div>
          {sub.subStatusNote && (
            <div className="text-xs" style={{ color: "#9CA3AF" }}>
              {sub.subStatusNote}
            </div>
          )}
        </div>
      );
    }
    return (
      <div className="mt-1 space-y-0.5">
        <div className="text-xs text-gray-500">{sub.spec}</div>
        <div className="text-xs" style={{ color: "#9CA3AF" }}>
          {sub.subStatusNote ?? sub.dateRange}
        </div>
      </div>
    );
  };

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{ border: "1.5px solid #F0F0F0", background: "#fff" }}
    >
      <div
        className="px-4 pt-3.5 pb-2 cursor-pointer active:bg-gray-50 transition-colors"
        onClick={() =>
          navigate(`/order/detail/${sub.type}`, {
            state: { productType: sub.type, orderId: sub.id },
          })
        }
      >
        <div className="flex items-start gap-3">
          <div
            className="w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 mt-0.5"
            style={{ background: `${sub.subStatusColor}15` }}
          >
            <i
              className={cfg.typeIcon}
              style={{ color: sub.subStatusColor, fontSize: 17 }}
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-gray-800 line-clamp-1">
                  {sub.type === "car"
                    ? sub.spec.split("·")[0].trim()
                    : sub.name}
                  {sub.type === "hotel" && (
                    <span className="font-normal text-gray-400 text-xs ml-1">
                      （{sub.spec.split("·")[0].trim()}）
                    </span>
                  )}
                </div>
                {renderDetail()}
              </div>
              <div className="flex flex-col items-end flex-shrink-0 gap-1.5">
                <span
                  className="text-xs px-2.5 py-0.5 rounded-full whitespace-nowrap font-semibold"
                  style={{
                    background: sub.subStatusBg,
                    color: sub.subStatusColor,
                  }}
                >
                  {sub.subStatus}
                </span>
                <span
                  className="text-sm font-bold"
                  style={{ color: "#374151" }}
                >
                  ¥{sub.totalPrice.toLocaleString()}
                </span>
                <span
                  className="text-xs flex items-center gap-0.5"
                  style={{ color: "#FF6B35" }}
                >
                  查看{cfg.typeLabel}
                  <i
                    className="ri-arrow-right-s-line"
                    style={{ fontSize: 13 }}
                  />
                </span>
              </div>
            </div>
          </div>
        </div>
        {steps.length > 0 && (
          <div className="flex items-center gap-0 mt-3 ml-12">
            {steps.map((s, i) => (
              <div key={s} className="flex items-center flex-1 last:flex-none">
                <div className="flex flex-col items-center">
                  <div
                    className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{
                      background:
                        i <= currentStepIdx ? sub.subStatusColor : "#E5E7EB",
                      fontSize: 8,
                    }}
                  >
                    {i < currentStepIdx && (
                      <i
                        className="ri-check-line text-white"
                        style={{ fontSize: 9 }}
                      />
                    )}
                    {i === currentStepIdx && (
                      <div className="w-1.5 h-1.5 rounded-full bg-white" />
                    )}
                  </div>
                  <span
                    style={{
                      fontSize: 9,
                      color:
                        i <= currentStepIdx ? sub.subStatusColor : "#9CA3AF",
                      whiteSpace: "nowrap",
                      marginTop: 2,
                    }}
                  >
                    {s.length > 6 ? s.split("·")[0] : s}
                  </span>
                </div>
                {i < steps.length - 1 && (
                  <div
                    className="flex-1 h-px mb-3 mx-0.5"
                    style={{
                      background:
                        i < currentStepIdx ? sub.subStatusColor : "#E5E7EB",
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div
        className="px-4 py-2.5 flex items-center justify-between"
        style={{ background: "#FAFBFC", borderTop: "1px solid #F3F4F6" }}
      >
        <span
          className="text-xs px-2.5 py-0.5 rounded-full"
          style={{ background: "#FFF7ED", color: "#EA580C" }}
        >
          {subRuleTags[sub.type] ?? ""}
        </span>
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-xs cursor-pointer whitespace-nowrap flex items-center gap-1"
          style={{ color: "#9CA3AF" }}
        >
          {expanded ? "收起操作" : "更多操作"}
          <i
            className={expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}
            style={{ fontSize: 14 }}
          />
        </button>
      </div>

      {expanded && (
        <div
          className="px-4 py-3"
          style={{ background: "#F8F9FC", borderTop: "1px solid #F0F0F0" }}
        >
          <div className="flex justify-between text-xs mb-2">
            <span className="text-gray-500">单价</span>
            <span className="text-gray-700">¥{sub.unitPrice} / 单位</span>
          </div>
          <div className="flex justify-between text-xs mb-2.5">
            <span className="text-gray-500">数量</span>
            <span className="text-gray-700">{sub.quantity}</span>
          </div>
          <div className="flex justify-between text-xs mb-3">
            <span className="text-gray-500">子订单号</span>
            <span className="text-gray-500 font-mono">{sub.id}</span>
          </div>

          {sub.type === "car" && sub.driverName && (
            <div className="mb-3 pt-3 border-t border-gray-100">
              <div className="text-xs font-semibold text-gray-500 mb-2">
                租车人信息
              </div>
              <div className="space-y-1.5">
                {[
                  {
                    label: "租车人",
                    value: sub.driverName,
                    icon: "ri-user-line",
                  },
                  {
                    label: "身份证",
                    value: sub.driverIdCard ?? "-",
                    icon: "ri-id-card-line",
                  },
                  {
                    label: "驾照号",
                    value: sub.driverLicense ?? "-",
                    icon: "ri-car-line",
                  },
                ].map((row) => (
                  <div
                    key={row.label}
                    className="flex items-center justify-between"
                  >
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <i className={row.icon} style={{ fontSize: 11 }} />
                      {row.label}
                    </span>
                    <span className="text-xs font-medium text-gray-600">
                      {row.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {sub.type === "car" &&
            sub.pickupPhotos &&
            sub.pickupPhotos.length > 0 && (
              <div className="pt-2 border-t border-gray-100">
                <InspectionPhotos
                  title="取车验车照片"
                  photos={sub.pickupPhotos}
                  color="#059669"
                />
              </div>
            )}
          {sub.type === "car" &&
            sub.returnPhotos &&
            sub.returnPhotos.length > 0 && (
              <div className="pt-2 border-t border-gray-100">
                <InspectionPhotos
                  title="还车验车照片"
                  photos={sub.returnPhotos}
                  color="#2563EB"
                />
              </div>
            )}

          {actions.length > 0 ? (
            <div className="flex gap-2 pt-2 border-t border-gray-100 mt-2">
              {actions.map((action) => {
                if (action === "__after_sale_done__") {
                  return (
                    <button
                      key={action}
                      disabled
                      className="flex-1 py-2 rounded-xl text-xs whitespace-nowrap font-medium cursor-default"
                      style={{ background: "#F3F4F6", color: "#9CA3AF" }}
                    >
                      <i
                        className="ri-customer-service-2-line mr-1"
                        style={{ fontSize: 11 }}
                      />
                      售后处理中
                    </button>
                  );
                }
                if (action === "__invoice_done__") {
                  return (
                    <button
                      key={action}
                      disabled
                      className="flex-1 py-2 rounded-xl text-xs whitespace-nowrap font-medium cursor-default"
                      style={{ background: "#F3F4F6", color: "#9CA3AF" }}
                    >
                      <i
                        className="ri-bill-line mr-1"
                        style={{ fontSize: 11 }}
                      />
                      已申请开票
                    </button>
                  );
                }
                return (
                  <button
                    key={action}
                    className="flex-1 py-2 rounded-xl text-xs cursor-pointer whitespace-nowrap font-medium"
                    style={{
                      background:
                        action === "售后" || action === "取消订单"
                          ? "#FEE2E2"
                          : action === "换车型"
                            ? "#EFF6FF"
                            : action === "开票"
                              ? "#FFF7ED"
                              : "#F3F4F6",
                      color:
                        action === "售后" || action === "取消订单"
                          ? "#DC2626"
                          : action === "换车型"
                            ? "#2563EB"
                            : action === "开票"
                              ? "#D97706"
                              : "#374151",
                    }}
                    onClick={() => {
                      if (action === "换车型")
                        navigate("/cars", { state: { replace: true } });
                      else if (action === "售后" || action === "补充凭证")
                        onAfterSale(sub.id);
                      else if (action === "开票") setShowInvoiceModal(true);
                    }}
                  >
                    {action === "售后" && (
                      <i
                        className="ri-customer-service-2-line mr-1"
                        style={{ fontSize: 11 }}
                      />
                    )}
                    {action === "开票" && (
                      <i
                        className="ri-bill-line mr-1"
                        style={{ fontSize: 11 }}
                      />
                    )}
                    {action}
                  </button>
                );
              })}
            </div>
          ) : (
            <div
              className="text-xs text-center pt-2 border-t border-gray-100"
              style={{ color: "#C4C8D0" }}
            >
              订单已取消，无操作权限
            </div>
          )}
          {showInvoiceModal && (
            <SubInvoiceModal
              subName={sub.name}
              subAmount={sub.totalPrice}
              onClose={() => setShowInvoiceModal(false)}
              onSubmitSuccess={() => {
                setShowInvoiceModal(false);
                onInvoiceApplied?.(sub.id);
              }}
            />
          )}

          {sub.type === "car" && sub.subStatus === "待取车" && (
            <div
              className="mt-2 flex items-center gap-1.5 px-2.5 py-2 rounded-xl"
              style={{ background: "#FFF7ED" }}
            >
              <i
                className="ri-information-line"
                style={{ color: "#EA580C", fontSize: 12 }}
              />
              <span className="text-xs" style={{ color: "#EA580C" }}>
                司机已接单，当前无法改期，可换车型
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── 整体开票弹窗（已完成订单整体角度） ──
function OverallInvoiceModal({
  subOrders,
  totalAmount,
  onClose,
  onSubmitSuccess,
}: {
  subOrders: SubOrder[];
  totalAmount: number;
  onClose: () => void;
  onSubmitSuccess: () => void;
}) {
  const [invoiceType, setInvoiceType] = useState<"personal" | "company">(
    "personal",
  );
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({ title: "", taxId: "", email: "" });
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }}
    >
      <div
        className="bg-white rounded-t-3xl w-full"
        style={{
          maxWidth: 480,
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div className="w-10 h-1 rounded-full bg-gray-200 mx-auto mt-3 flex-shrink-0" />
        <div
          className="flex items-center justify-between px-5 pt-4 pb-3 flex-shrink-0"
          style={{ borderBottom: "1px solid #F5F5F5" }}
        >
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer"
            style={{ background: "#F4F5F9" }}
          >
            <i
              className="ri-close-line"
              style={{ color: "#374151", fontSize: 18 }}
            />
          </button>
          <span className="text-base font-bold text-gray-900">申请开票</span>
          <div className="w-8" />
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-5">
          {submitted ? (
            <div className="flex flex-col items-center py-6">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mb-3"
                style={{ background: "#FFF7ED" }}
              >
                <i
                  className="ri-bill-line"
                  style={{ color: "#D97706", fontSize: 32 }}
                />
              </div>
              <div className="text-base font-bold text-gray-900 mb-1">
                开票申请已提交
              </div>
              <div className="text-xs text-gray-500 text-center mb-1">
                共 {subOrders.length} 个产品 · ¥{totalAmount.toLocaleString()}
              </div>
              <div className="text-xs text-gray-500 text-center">
                电子发票将在1-3个工作日内发送至：{formData.email}
              </div>
              <button
                onClick={onSubmitSuccess}
                className="mt-5 px-8 py-2.5 rounded-full text-sm font-semibold text-white cursor-pointer whitespace-nowrap"
                style={{ background: "#D97706" }}
              >
                知道了
              </button>
            </div>
          ) : (
            <>
              <div
                className="mb-3 p-3 rounded-xl"
                style={{ background: "#FFF7ED" }}
              >
                <div className="text-xs text-amber-700 font-semibold mb-1.5">
                  开票产品（共{subOrders.length}项）
                </div>
                <div className="space-y-1">
                  {subOrders.map((sub) => (
                    <div key={sub.id} className="flex justify-between text-xs">
                      <span className="text-gray-600 truncate flex-1 mr-2">
                        {sub.name}
                      </span>
                      <span className="font-medium text-gray-800 flex-shrink-0">
                        ¥{sub.totalPrice.toLocaleString()}
                      </span>
                    </div>
                  ))}
                  <div className="flex justify-between text-xs pt-1.5 border-t border-amber-100 mt-1">
                    <span className="font-semibold text-gray-700">
                      合计开票金额
                    </span>
                    <span className="font-bold" style={{ color: "#EF4444" }}>
                      ¥{totalAmount.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mb-4">
                {[
                  { val: "personal" as const, label: "个人发票" },
                  { val: "company" as const, label: "企业发票" },
                ].map(({ val, label }) => (
                  <button
                    key={val}
                    onClick={() => setInvoiceType(val)}
                    className="flex-1 py-2.5 rounded-xl text-sm cursor-pointer font-medium whitespace-nowrap"
                    style={{
                      background: invoiceType === val ? "#FFF7ED" : "#F4F5F9",
                      color: invoiceType === val ? "#D97706" : "#374151",
                      border:
                        invoiceType === val
                          ? "1.5px solid #FED7AA"
                          : "1.5px solid transparent",
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="space-y-3 mb-5">
                <div>
                  <div className="text-xs text-gray-500 mb-1.5">
                    {invoiceType === "company" ? "企业名称" : "发票抬头"}
                  </div>
                  <input
                    className="w-full rounded-xl px-3 py-2.5 text-sm outline-none"
                    style={{
                      background: "#F8F8F8",
                      border: "1.5px solid #F0F0F0",
                    }}
                    placeholder={
                      invoiceType === "company"
                        ? "请输入企业名称"
                        : "请输入您的姓名"
                    }
                    value={formData.title}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, title: e.target.value }))
                    }
                  />
                </div>
                {invoiceType === "company" && (
                  <div>
                    <div className="text-xs text-gray-500 mb-1.5">
                      税务识别号
                    </div>
                    <input
                      className="w-full rounded-xl px-3 py-2.5 text-sm outline-none"
                      style={{
                        background: "#F8F8F8",
                        border: "1.5px solid #F0F0F0",
                      }}
                      placeholder="请输入18位税务识别号"
                      value={formData.taxId}
                      onChange={(e) =>
                        setFormData((p) => ({ ...p, taxId: e.target.value }))
                      }
                    />
                  </div>
                )}
                <div>
                  <div className="text-xs text-gray-500 mb-1.5">接收邮箱</div>
                  <input
                    className="w-full rounded-xl px-3 py-2.5 text-sm outline-none"
                    style={{
                      background: "#F8F8F8",
                      border: "1.5px solid #F0F0F0",
                    }}
                    placeholder="请输入接收发票的邮箱"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, email: e.target.value }))
                    }
                  />
                </div>
              </div>
              <button
                onClick={() => {
                  if (formData.title && formData.email) setSubmitted(true);
                }}
                className="w-full py-3 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap"
                style={{
                  background:
                    formData.title && formData.email ? "#D97706" : "#F0F0F0",
                  color: formData.title && formData.email ? "#fff" : "#9CA3AF",
                }}
              >
                提交开票申请
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── 售后弹窗（组合订单专用，多选产品+全部产品） ──
interface MultiAfterSaleModalProps {
  subOrders: SubOrder[];
  defaultSubId?: string;
  isOverall?: boolean;
  onClose: () => void;
  onSubmitSuccess?: (subIds: string[]) => void;
}
function MultiAfterSaleModal({
  subOrders,
  defaultSubId,
  isOverall = false,
  onClose,
  onSubmitSuccess,
}: MultiAfterSaleModalProps) {
  const initIds = isOverall
    ? subOrders.map((s) => s.id)
    : defaultSubId
      ? [defaultSubId]
      : [subOrders[0]?.id ?? ""];

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set(initIds));
  const [allSelected, setAllSelected] = useState(isOverall);
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [description, setDescription] = useState("");
  const maxInitAmount = subOrders
    .filter((s) => new Set(initIds).has(s.id))
    .reduce((sum, s) => sum + s.totalPrice, 0);
  const [refundAmount, setRefundAmount] = useState(
    isOverall ? String(maxInitAmount) : "",
  );
  const [amountError, setAmountError] = useState("");
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState(false);

  const subTypeLabel: Record<string, string> = {
    hotel: "酒店",
    car: "租车",
    route: "线路",
    guide: "导游",
  };
  const subTypeIcon: Record<string, string> = {
    hotel: "ri-hotel-bed-line",
    car: "ri-car-line",
    route: "ri-route-line",
    guide: "ri-user-star-line",
  };
  const reasons = [
    "服务与描述不符",
    "导游/司机态度差",
    "行程缺失内容",
    "安全问题",
    "设施损坏/安全隐患",
    "费用纠纷",
    "其他原因",
  ];
  const mockImageUrls = [
    "https://readdy.ai/api/search-image?query=hotel%20room%20complaint%20issue%20documentation%20evidence%20photo&width=200&height=150&seq=mas001&orientation=landscape",
    "https://readdy.ai/api/search-image?query=travel%20service%20problem%20complaint%20evidence%20documentation%20photo&width=200&height=150&seq=mas002&orientation=landscape",
    "https://readdy.ai/api/search-image?query=trip%20service%20quality%20issue%20complaint%20documentation%20photo&width=200&height=150&seq=mas003&orientation=landscape",
  ];

  const maxAmount = subOrders
    .filter((s) => selectedIds.has(s.id))
    .reduce((sum, s) => sum + s.totalPrice, 0);

  const toggleAll = () => {
    if (allSelected) {
      setAllSelected(false);
      setSelectedIds(new Set());
    } else {
      setAllSelected(true);
      setSelectedIds(new Set(subOrders.map((s) => s.id)));
    }
    setRefundAmount("");
    setAmountError("");
  };

  const toggleSub = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
      setAllSelected(false);
    } else {
      next.add(id);
      if (next.size === subOrders.length) setAllSelected(true);
    }
    setSelectedIds(next);
    setRefundAmount("");
    setAmountError("");
  };

  const handleAmountChange = (val: string) => {
    setRefundAmount(val);
    const num = parseFloat(val);
    if (val && (isNaN(num) || num <= 0)) {
      setAmountError("请输入有效金额");
    } else if (num > maxAmount) {
      setAmountError(`不可超过所选产品总额 ¥${maxAmount.toLocaleString()}`);
    } else {
      setAmountError("");
    }
  };

  const canSubmit = !!(
    selectedIds.size > 0 &&
    selectedReason &&
    description.trim() &&
    refundAmount &&
    !amountError
  );
  const selectedSubNames = subOrders
    .filter((s) => selectedIds.has(s.id))
    .map((s) => s.name)
    .join("、");

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }}
    >
      <div
        className="bg-white rounded-t-3xl w-full"
        style={{
          maxWidth: 480,
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div className="w-10 h-1 rounded-full bg-gray-200 mx-auto mt-3 flex-shrink-0" />
        <div
          className="flex items-center justify-between px-5 pt-4 pb-3 flex-shrink-0"
          style={{ borderBottom: "1px solid #F5F5F5" }}
        >
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer"
            style={{ background: "#F4F5F9" }}
          >
            <i
              className="ri-close-line"
              style={{ color: "#374151", fontSize: 18 }}
            />
          </button>
          <span className="text-base font-bold text-gray-900">申请售后</span>
          <div className="w-8" />
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5">
          {submitted ? (
            <div className="flex flex-col items-center py-8">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mb-3"
                style={{ background: "#EFF6FF" }}
              >
                <i
                  className="ri-customer-service-2-line"
                  style={{ color: "#2563EB", fontSize: 32 }}
                />
              </div>
              <div className="text-base font-bold text-gray-900 mb-1">
                售后申请已提交
              </div>
              <div className="text-xs text-gray-500 text-center mb-4">
                服务商将尽快处理您的申请，请保持手机畅通
              </div>
              <div
                className="p-3 rounded-2xl w-full"
                style={{ background: "#F8F8F8" }}
              >
                <div className="flex justify-between text-xs mb-1.5">
                  <span className="text-gray-400 flex-shrink-0">售后产品</span>
                  <span className="font-medium text-gray-700 text-right ml-3 leading-relaxed">
                    {selectedSubNames}
                  </span>
                </div>
                <div className="flex justify-between text-xs mb-1.5">
                  <span className="text-gray-400">售后原因</span>
                  <span className="font-medium text-gray-700">
                    {selectedReason}
                  </span>
                </div>
                <div className="flex justify-between text-xs mb-1.5">
                  <span className="text-gray-400">申请退款</span>
                  <span className="font-bold" style={{ color: "#EF4444" }}>
                    ¥{parseFloat(refundAmount).toLocaleString()}
                  </span>
                </div>
                {uploadedImages.length > 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-400">凭证图片</span>
                    <span className="font-medium text-gray-700">
                      已上传 {uploadedImages.length} 张
                    </span>
                  </div>
                )}
              </div>
              <button
                onClick={onClose}
                className="mt-5 px-8 py-2.5 rounded-full text-sm font-semibold text-white cursor-pointer whitespace-nowrap"
                style={{ background: "#2563EB" }}
              >
                知道了
              </button>
            </div>
          ) : (
            <>
              <div
                className="mb-4 p-3 rounded-2xl"
                style={{ background: "#F0F4FF" }}
              >
                <p
                  className="text-xs leading-relaxed"
                  style={{ color: "#374151" }}
                >
                  <i
                    className="ri-information-line mr-1"
                    style={{ color: "#2563EB" }}
                  ></i>
                  可选择<strong>一个或多个产品</strong>
                  申请售后，也可勾选「全部产品」统一申请退款。
                </p>
              </div>

              {/* 多选产品 */}
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-gray-700">
                  选择售后产品 <span className="text-red-500">*</span>
                </span>
                <button
                  onClick={toggleAll}
                  className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs cursor-pointer font-medium whitespace-nowrap transition-all"
                  style={{
                    background: allSelected ? "#EFF6FF" : "#F4F5F9",
                    color: allSelected ? "#2563EB" : "#6B7280",
                    border: allSelected
                      ? "1.5px solid #BFDBFE"
                      : "1.5px solid transparent",
                  }}
                >
                  <div
                    className="w-3.5 h-3.5 rounded flex items-center justify-center"
                    style={{
                      background: allSelected ? "#2563EB" : "transparent",
                      border: allSelected ? "none" : "1.5px solid #D1D5DB",
                    }}
                  >
                    {allSelected && (
                      <i
                        className="ri-check-line text-white"
                        style={{ fontSize: 9 }}
                      />
                    )}
                  </div>
                  全部产品
                </button>
              </div>
              <div className="space-y-2 mb-3">
                {subOrders.map((sub) => {
                  const checked = selectedIds.has(sub.id);
                  return (
                    <button
                      key={sub.id}
                      onClick={() => toggleSub(sub.id)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all cursor-pointer"
                      style={{
                        background: checked ? "#EFF6FF" : "#F8F8F8",
                        border: checked
                          ? "1.5px solid #BFDBFE"
                          : "1.5px solid transparent",
                      }}
                    >
                      <div
                        className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0"
                        style={{
                          background: checked ? "#2563EB" : "transparent",
                          border: checked ? "none" : "1.5px solid #D1D5DB",
                        }}
                      >
                        {checked && (
                          <i
                            className="ri-check-line text-white"
                            style={{ fontSize: 9 }}
                          />
                        )}
                      </div>
                      <div
                        className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0"
                        style={{ background: sub.subStatusBg }}
                      >
                        <i
                          className={subTypeIcon[sub.type] ?? "ri-file-line"}
                          style={{ color: sub.subStatusColor, fontSize: 15 }}
                        ></i>
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">
                          {sub.name}
                        </p>
                        <p className="text-xs text-gray-400">
                          {subTypeLabel[sub.type] ?? sub.type} · ¥
                          {sub.totalPrice.toLocaleString()}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
              {selectedIds.size > 0 && (
                <div
                  className="flex justify-between items-center mb-4 px-3 py-2 rounded-xl"
                  style={{ background: "#F0F4FF" }}
                >
                  <span className="text-xs" style={{ color: "#2563EB" }}>
                    已选 {selectedIds.size} 项产品
                  </span>
                  <span
                    className="text-sm font-bold"
                    style={{ color: "#EF4444" }}
                  >
                    合计 ¥{maxAmount.toLocaleString()}
                  </span>
                </div>
              )}

              {/* 售后原因 */}
              <div className="text-xs font-semibold text-gray-700 mb-2">
                售后原因 <span className="text-red-500">*</span>
              </div>
              <div className="flex flex-wrap gap-2 mb-4">
                {reasons.map((r) => (
                  <button
                    key={r}
                    onClick={() => setSelectedReason(r)}
                    className="px-3 py-2 rounded-xl text-xs cursor-pointer font-medium whitespace-nowrap transition-all"
                    style={{
                      background: selectedReason === r ? "#EFF6FF" : "#F4F5F9",
                      color: selectedReason === r ? "#2563EB" : "#374151",
                      border:
                        selectedReason === r
                          ? "1.5px solid #BFDBFE"
                          : "1.5px solid transparent",
                    }}
                  >
                    {r}
                  </button>
                ))}
              </div>

              {/* 问题描述 */}
              <div className="text-xs font-semibold text-gray-700 mb-2">
                问题描述 <span className="text-red-500">*</span>
              </div>
              <textarea
                className="w-full rounded-2xl p-3 text-xs resize-none outline-none"
                style={{
                  background: "#F8F8F8",
                  border: "1.5px solid #F0F0F0",
                  minHeight: 80,
                  color: "#374151",
                }}
                placeholder="请详细描述您遇到的问题，最多500字..."
                value={description}
                maxLength={500}
                onChange={(e) => setDescription(e.target.value)}
              />
              <div className="flex justify-end text-xs text-gray-400 mt-1 mb-4">
                {description.length}/500
              </div>

              {/* 申请退款金额 */}
              <div className="text-xs font-semibold text-gray-700 mb-2">
                申请退款金额 <span className="text-red-500">*</span>
                {maxAmount > 0 && (
                  <span className="text-gray-400 font-normal ml-1">
                    （最多 ¥{maxAmount.toLocaleString()}）
                  </span>
                )}
              </div>
              <div className="relative mb-1">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">
                  ¥
                </span>
                <input
                  type="number"
                  value={refundAmount}
                  onChange={(e) => handleAmountChange(e.target.value)}
                  placeholder={
                    selectedIds.size > 0
                      ? `最多 ¥${maxAmount.toLocaleString()}`
                      : "请先选择产品"
                  }
                  disabled={selectedIds.size === 0}
                  className="w-full pl-8 pr-4 py-2.5 rounded-xl text-sm outline-none"
                  style={{
                    background: "#F8F8F8",
                    border: amountError
                      ? "1.5px solid #EF4444"
                      : "1.5px solid #F0F0F0",
                    color: "#374151",
                  }}
                  min={0}
                  step={0.01}
                />
              </div>
              {amountError ? (
                <p className="text-xs mb-4" style={{ color: "#EF4444" }}>
                  {amountError}
                </p>
              ) : (
                <div className="mb-4" />
              )}

              {/* 凭证上传 */}
              <div className="text-xs font-semibold text-gray-700 mb-2">
                上传凭证图片{" "}
                <span className="text-gray-400 font-normal">
                  （可选，最多6张）
                </span>
              </div>
              <div className="flex flex-wrap gap-2 mb-1">
                {uploadedImages.map((img, i) => (
                  <div
                    key={i}
                    className="relative rounded-2xl overflow-hidden"
                    style={{ width: 72, height: 60 }}
                  >
                    <img
                      src={img}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                    <button
                      onClick={() =>
                        setUploadedImages((prev) =>
                          prev.filter((_, idx) => idx !== i),
                        )
                      }
                      className="absolute top-1 right-1 w-4 h-4 flex items-center justify-center rounded-full cursor-pointer"
                      style={{ background: "rgba(0,0,0,0.55)" }}
                    >
                      <i
                        className="ri-close-line text-white"
                        style={{ fontSize: 10 }}
                      ></i>
                    </button>
                  </div>
                ))}
                {uploadedImages.length < 6 && (
                  <button
                    onClick={() => {
                      if (uploadedImages.length < 6)
                        setUploadedImages((prev) => [
                          ...prev,
                          mockImageUrls[prev.length % mockImageUrls.length],
                        ]);
                    }}
                    className="flex flex-col items-center justify-center rounded-2xl cursor-pointer"
                    style={{
                      width: 72,
                      height: 60,
                      background: "#F4F5F9",
                      border: "1.5px dashed #D1D5DB",
                    }}
                  >
                    <i
                      className="ri-add-line"
                      style={{ color: "#9CA3AF", fontSize: 18 }}
                    ></i>
                    <span
                      style={{ fontSize: 10, color: "#9CA3AF", marginTop: 2 }}
                    >
                      添加图片
                    </span>
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-400 mb-5">
                支持 JPG/PNG，建议上传订单凭证、服务问题截图等
              </p>

              <button
                onClick={() => {
                  if (canSubmit) {
                    setSubmitted(true);
                    onSubmitSuccess?.(Array.from(selectedIds));
                  }
                }}
                disabled={!canSubmit}
                className="w-full py-3 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap"
                style={{
                  background: canSubmit ? "#2563EB" : "#F0F0F0",
                  color: canSubmit ? "#fff" : "#9CA3AF",
                }}
              >
                提交售后申请
                {selectedIds.size > 1 ? `（${selectedIds.size}项产品）` : ""}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── 主状态补充小字 ──
function getMainStatusNote(status: string, subOrders?: SubOrder[]) {
  const completed =
    subOrders?.filter((s) =>
      ["已完成", "已完结", "已离店", "已还车"].some((k) =>
        s.subStatus.includes(k),
      ),
    ).length ?? 0;
  const afterSale =
    subOrders?.filter((s) => s.subStatus === "售后中").length ?? 0;
  const cancelled =
    subOrders?.filter((s) => s.subStatus === "已取消").length ?? 0;
  const total = subOrders?.length ?? 0;

  switch (status) {
    case "待出行":
      return "所有服务待使用";
    case "部分履约":
      return `${completed}项服务已完成，${total - completed}项待使用`;
    case "全部履约":
      return "所有服务已完成，待结算";
    case "已完成":
      return "订单已结算，可评价或申请售后开票";
    case "部分取消":
      return `${cancelled}项服务已取消，剩余服务正常`;
    case "全额取消":
      return "订单已全额取消，可查看详情";
    case "售后中":
      return `${afterSale}项服务售后处理中，可跟进`;
    default:
      return null;
  }
}

export default function OrderDetailMultiPage() {
  const navigate = useNavigate();
  // 优先显示已完成的组合订单
  const order =
    orderList.find((o) => o.type === "multi" && o.mainStatus === "已完成") ??
    orderList.find((o) => o.type === "multi") ??
    orderList[0];
  const [showFee, setShowFee] = useState(false);
  const [afterSaleSubId, setAfterSaleSubId] = useState<string | null>(null);
  const [afterSaleOverall, setAfterSaleOverall] = useState(false);
  const [afterSaleAppliedIds, setAfterSaleAppliedIds] = useState<Set<string>>(
    new Set(),
  );
  const [invoiceAppliedIds, setInvoiceAppliedIds] = useState<Set<string>>(
    new Set(),
  );
  const [showOverallInvoice, setShowOverallInvoice] = useState(false);
  const [overallAfterSaleDone, setOverallAfterSaleDone] = useState(false);
  const [overallInvoiceDone, setOverallInvoiceDone] = useState(false);

  const statusNote = getMainStatusNote(order.mainStatus, order.subOrders);
  const isCompleted = order.mainStatus === "已完成";
  const completedSubs = (order.subOrders ?? []).filter((s) =>
    ["已完成", "已完结", "已离店", "已还车"].some((k) =>
      s.subStatus.includes(k),
    ),
  );

  const canFullRefund = order.subOrders?.every((s) =>
    ["待酒店确认", "待派车", "待成团", "待确认"].includes(s.subStatus),
  );

  return (
    <div
      className="min-h-screen pb-[calc(env(safe-area-inset-bottom,0px)+112px)]"
      style={{
        background: "#F4F5F9",
        fontFamily: "'Inter','SF Pro Text',system-ui,sans-serif",
      }}
    >
      {/* Header */}
      <div
        className="relative overflow-hidden sticky top-0 z-40"
        style={{
          background:
            "linear-gradient(145deg,#1A2238 0%,#2D3561 70%,#3D5A80 100%)",
        }}
      >
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div
            className="absolute rounded-full"
            style={{
              width: 240,
              height: 240,
              top: -60,
              right: -40,
              background: "rgba(255,107,53,0.12)",
              filter: "blur(35px)",
            }}
          />
        </div>
        <div className="relative px-4 pb-4" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 48px)" }}>
          <div className="flex items-center gap-3 mb-2">
            <button
              onClick={() => navigate(-1)}
              className="w-9 h-9 flex items-center justify-center rounded-full cursor-pointer flex-shrink-0"
              style={{
                background: "rgba(255,255,255,0.1)",
                border: "1px solid rgba(255,255,255,0.15)",
              }}
            >
              <i
                className="ri-arrow-left-line"
                style={{ fontSize: 18, color: "#fff" }}
              />
            </button>
            <div className="flex-1 min-w-0">
              <h1
                className="font-bold text-white leading-snug"
                style={{ fontSize: 15 }}
              >
                {order.destination}组合订单
                <span
                  className="font-normal ml-1.5"
                  style={{ fontSize: 12, color: "rgba(255,255,255,0.45)" }}
                >
                  · 订单号：{order.id}
                </span>
              </h1>
              <div
                className="text-xs mt-0.5"
                style={{ color: "rgba(255,255,255,0.4)", fontSize: 11 }}
              >
                创建时间：{order.time} | 总金额：¥{order.total.toLocaleString()}
                .00
              </div>
            </div>
            <span
              className="text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap flex-shrink-0"
              style={{
                background: "rgba(255,255,255,0.12)",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.18)",
              }}
            >
              {order.mainStatus}
            </span>
          </div>
          {statusNote && (
            <div
              className="ml-12 mb-2 text-xs"
              style={{ color: "rgba(255,255,255,0.55)" }}
            >
              {statusNote}
            </div>
          )}
          {/* {order.mainStatus === "待签约" && (
            <div className="ml-12 mb-2">
              <button onClick={() => navigate("/contracts")} className="text-xs px-3 py-1 rounded-full cursor-pointer whitespace-nowrap font-medium" style={{ background: "rgba(14,165,233,0.25)", color: "#7DD3FC", border: "1px solid rgba(125,211,252,0.3)" }}>去签约</button>
            </div>
          )} */}
          <ProgressBar step={order.mainStatusStep} type="tour" />
        </div>
      </div>

      {/* 子订单区域 */}
      <div className="mx-4 mt-4">
        <div className="flex items-center gap-2 mb-3">
          <div
            className="w-1 h-4 rounded-full"
            style={{ background: "#FF6B35" }}
          />
          <span className="text-sm font-bold text-gray-900">
            订单包含以下服务
          </span>
          <span className="text-xs text-gray-400">
            共 {order.subOrders?.length} 项
          </span>
          <span className="text-xs ml-auto" style={{ color: "#9CA3AF" }}>
            点击卡片查看详情
          </span>
        </div>
        <div className="space-y-3">
          {(order.subOrders ?? []).map((sub) => (
            <SubOrderCard
              key={sub.id}
              sub={sub}
              onAfterSale={(id) => {
                setAfterSaleSubId(id);
                setAfterSaleOverall(false);
              }}
              isAfterSaleApplied={afterSaleAppliedIds.has(sub.id)}
              isInvoiceApplied={invoiceAppliedIds.has(sub.id)}
              onInvoiceApplied={(id) =>
                setInvoiceAppliedIds((prev) => {
                  const s = new Set(prev);
                  s.add(id);
                  return s;
                })
              }
            />
          ))}
        </div>

        {/* 售后弹窗 */}
        {(afterSaleSubId !== null || afterSaleOverall) && order.subOrders && (
          <MultiAfterSaleModal
            subOrders={isCompleted ? completedSubs : (order.subOrders ?? [])}
            defaultSubId={afterSaleSubId ?? undefined}
            isOverall={afterSaleOverall}
            onClose={() => {
              setAfterSaleSubId(null);
              setAfterSaleOverall(false);
            }}
            onSubmitSuccess={(subIds) => {
              subIds.forEach((id) => {
                setAfterSaleAppliedIds((prev) => {
                  const s = new Set(prev);
                  s.add(id);
                  return s;
                });
                setInvoiceAppliedIds((prev) => {
                  const s = new Set(prev);
                  s.delete(id);
                  return s;
                });
              });
              if (afterSaleOverall) setOverallAfterSaleDone(true);
              setAfterSaleSubId(null);
              setAfterSaleOverall(false);
            }}
          />
        )}
        {showOverallInvoice && order.subOrders && (
          <OverallInvoiceModal
            subOrders={completedSubs}
            totalAmount={completedSubs.reduce((s, o) => s + o.totalPrice, 0)}
            onClose={() => setShowOverallInvoice(false)}
            onSubmitSuccess={() => {
              setShowOverallInvoice(false);
              setOverallInvoiceDone(true);
            }}
          />
        )}
      </div>

      {/* 费用明细 */}
      <div className="mx-4 mt-4 bg-white rounded-2xl overflow-hidden">
        <button
          onClick={() => setShowFee(!showFee)}
          className="w-full flex items-center justify-between px-4 py-3.5 cursor-pointer"
        >
          <span className="text-sm font-bold text-gray-900">费用明细</span>
          <div className="flex items-center gap-2">
            <span className="font-bold text-base" style={{ color: "#EF4444" }}>
              ¥{order.total.toLocaleString()}
            </span>
            <i
              className={
                showFee ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"
              }
              style={{ color: "#9CA3AF", fontSize: 18 }}
            />
          </div>
        </button>
        {showFee && (
          <div className="px-4 pb-4 space-y-1.5 border-t border-gray-50">
            {(order.subOrders ?? []).map((sub) => (
              <div
                key={sub.id}
                className="flex items-center justify-between py-2"
                style={{ borderBottom: "1px solid #F8F8F8" }}
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm">{sub.icon}</span>
                  <div>
                    <span className="text-xs text-gray-600">{sub.name}</span>
                    <span className="text-xs text-gray-400 ml-1">
                      × {sub.quantity}
                    </span>
                  </div>
                </div>
                <span className="text-xs font-semibold text-gray-800">
                  ¥{sub.totalPrice.toLocaleString()}
                </span>
              </div>
            ))}
            <div className="flex items-center justify-between py-1.5">
              <span
                className="text-xs font-medium"
                style={{ color: "#059669" }}
              >
                组合优惠
              </span>
              <span
                className="text-xs font-semibold"
                style={{ color: "#059669" }}
              >
                -¥160
              </span>
            </div>
            <div
              className="flex items-center justify-between pt-2"
              style={{ borderTop: "1px solid #F0F0F0" }}
            >
              <span className="text-sm font-bold text-gray-900">实付总计</span>
              <span className="font-bold text-lg" style={{ color: "#EF4444" }}>
                ¥{order.total.toLocaleString()}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* 服务与凭证 */}
      <div className="mx-4 mt-4 bg-white rounded-2xl p-4 mb-4">
        <div className="text-sm font-bold text-gray-900 mb-3">服务与凭证</div>
        <div>
          {[
            {
              icon: "ri-file-text-line",
              label: "电子合同",
              sub: "查看签约合同",
              color: "#2563EB",
              path: "/contracts",
            },
            {
              icon: "ri-shield-line",
              label: "保险保单",
              sub: "行程安全保障",
              color: "#059669",
              path: "/insurance",
            },
            {
              icon: "ri-bill-line",
              label: "电子发票",
              sub: "申请和查看发票",
              color: "#D97706",
              path: "/invoices",
            },
            {
              icon: "ri-customer-service-2-line",
              label: "联系客服",
              sub: "7×24小时在线",
              color: "#7C3AED",
              path: "/",
            },
          ].map((item, idx) => (
            <button
              key={item.label}
              onClick={() => navigate(item.path)}
              className="w-full flex items-center justify-between py-3 cursor-pointer active:bg-gray-50 transition-colors"
              style={{ borderTop: idx > 0 ? "1px solid #F8F8F8" : "none" }}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-9 h-9 flex items-center justify-center rounded-xl"
                  style={{ background: `${item.color}14` }}
                >
                  <i
                    className={item.icon}
                    style={{ color: item.color, fontSize: 17 }}
                  />
                </div>
                <div className="text-left">
                  <div className="text-sm text-gray-800 font-medium">
                    {item.label}
                  </div>
                  <div className="text-xs text-gray-400">{item.sub}</div>
                </div>
              </div>
              <i
                className="ri-arrow-right-s-line"
                style={{ color: "#D8DAE0", fontSize: 18 }}
              />
            </button>
          ))}
        </div>
      </div>

      {/* 底部操作 */}
      <div
        className="fixed bottom-0 left-0 right-0 bg-white px-4 z-40"
        style={{ borderTop: "1px solid #F5F5F5", paddingTop: "8px", paddingBottom: "calc(8px + env(safe-area-inset-bottom, 0px))" }}
      >
        {isCompleted ? (
          <div className="space-y-2">
            {/* 整体售后/开票互斥行 */}
            <div className="flex gap-2">
              {overallAfterSaleDone ? (
                <div
                  className="flex-1 py-2.5 rounded-full text-sm font-semibold text-center flex items-center justify-center gap-1.5"
                  style={{ background: "#F3F4F6", color: "#9CA3AF" }}
                >
                  <i
                    className="ri-customer-service-2-line"
                    style={{ fontSize: 14 }}
                  />
                  售后处理中
                </div>
              ) : !overallInvoiceDone ? (
                <button
                  onClick={() => {
                    setAfterSaleOverall(true);
                    setAfterSaleSubId(null);
                  }}
                  className="flex-1 py-2.5 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5"
                  style={{ background: "#FEE2E2", color: "#DC2626" }}
                >
                  <i
                    className="ri-customer-service-2-line"
                    style={{ fontSize: 14 }}
                  />
                  申请售后
                </button>
              ) : null}

              {overallInvoiceDone ? (
                <div
                  className="flex-1 py-2.5 rounded-full text-sm font-semibold text-center flex items-center justify-center gap-1.5"
                  style={{ background: "#F3F4F6", color: "#9CA3AF" }}
                >
                  <i className="ri-bill-line" style={{ fontSize: 14 }} />
                  已申请开票
                </div>
              ) : !overallAfterSaleDone ? (
                <button
                  onClick={() => setShowOverallInvoice(true)}
                  className="flex-1 py-2.5 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5"
                  style={{ background: "#FFF7ED", color: "#D97706" }}
                >
                  <i className="ri-bill-line" style={{ fontSize: 14 }} />
                  申请开票
                </button>
              ) : null}
            </div>
            {/* 去评价 + 返回列表 */}
            <div className="flex gap-2">
              <button
                onClick={() => navigate("/orders")}
                className="flex-1 py-2.5 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap"
                style={{ background: "#F4F5F9", color: "#6B7280" }}
              >
                返回列表
              </button>
              <button
                className="flex-1 py-2.5 rounded-full text-sm font-semibold text-white cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5"
                style={{ background: "#FF6B35" }}
              >
                <i className="ri-star-line" style={{ fontSize: 14 }} />
                去评价
              </button>
            </div>
          </div>
        ) : canFullRefund ? (
          <div className="flex gap-3">
            <button
              onClick={() => navigate("/orders")}
              className="flex-1 py-3 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap"
              style={{ background: "#F4F5F9", color: "#6B7280" }}
            >
              返回列表
            </button>
            <button
              className="flex-1 py-3 rounded-full text-sm font-semibold text-white cursor-pointer whitespace-nowrap"
              style={{ background: "#FF6B35" }}
            >
              联系客服
            </button>
          </div>
        ) : (
          <div className="flex gap-3">
            <button
              onClick={() => navigate("/orders")}
              className="flex-1 py-3 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap"
              style={{ background: "#F4F5F9", color: "#6B7280" }}
            >
              返回列表
            </button>
            <button
              className="flex-1 py-3 rounded-full text-sm font-semibold text-white cursor-pointer whitespace-nowrap"
              style={{ background: "#FF6B35" }}
            >
              联系客服
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
